import * as Icons from '@ant-design/icons';
import clsx from 'clsx';
import useNotification from 'hooks/useNotification';
import {
  Button,
  Card,
  Drawer,
  Flex,
  Form,
  Input,
  Space,
  Typography
} from 'antd';
import { CustomModal } from 'components/modal/CustomModal';
import { DrawerApprovesHistory } from './DrawerApprovesHistory';
import { formatDate } from 'services/date';
import { useCallback, useEffect, useState } from 'react';
import { useCertificateStore } from '../../certificate.store';
import { useCustomMutation, useList } from '@refinedev/core';

interface IApproves {
  status: 'pending' | 'approved' | 'rejected';
  date: string;
  user: {
    id: string;
    login: string;
    name: string;
  } | null;
}

const INITIAL_APPROVES = {
  secretariat: {
    status: 'pending',
    date: '',
    user: null
  } as IApproves,
  csc: {
    status: 'pending',
    date: '',
    user: null
  } as IApproves
};

export const DrawerApproves = () => {
  const {
    modals,
    enrollment,
    drawers: { approves: approvesDrawer, approvesHistory },
    openRejectCertificateModal,
    closeRejectCertificateModal,
    refreshCurrentPage,
    closeApprovesDrawer,
    openApprovesHistoryDrawer
  } = useCertificateStore();

  const { data: certificationApprovals, isLoading } = useList({
    resource: `graduate/courses/enrollments/${enrollment?.id}/certification_approvals`
  });

  const { showSuccess, showError } = useNotification();
  const { mutateAsync } = useCustomMutation();
  const [type, setType] = useState<'secretariat' | 'csc'>('secretariat');

  const [approves, setApproves] = useState(INITIAL_APPROVES);

  const [form] = Form.useForm();

  const handleApprove = useCallback(
    async (type: 'secretariat' | 'csc') => {
      try {
        await mutateAsync({
          url: `admin/graduate/courses/enrollments/${enrollment?.id}/certification_approvals`,
          method: 'patch',
          values: {
            kind: type,
            approved: true,
            observation: ''
          }
        });

        setApproves(prev => ({
          ...prev,
          [type]: { ...prev[type], status: 'approved' }
        }));

        showSuccess('Certificado aprovado com sucesso');
        refreshCurrentPage();
      } catch {
        showError('Erro ao aprovar certificado');
      }
    },
    [mutateAsync, showError, showSuccess, enrollment?.id, refreshCurrentPage]
  );

  const handleReject = useCallback(
    async (type: 'secretariat' | 'csc') => {
      try {
        await mutateAsync({
          url: `admin/graduate/courses/enrollments/${enrollment?.id}/certification_approvals`,
          method: 'patch',
          values: {
            kind: type,
            approved: false,
            observation: form.getFieldValue('observation')
          }
        });

        setApproves(prev => ({
          ...prev,
          [type]: { ...prev[type], status: 'rejected' }
        }));

        showSuccess('Certificado rejeitado com sucesso');
        form.resetFields();
        closeRejectCertificateModal();
        refreshCurrentPage();
      } catch {
        showError('Erro ao rejeitar certificado');
      }
    },
    [
      mutateAsync,
      showError,
      showSuccess,
      enrollment?.id,
      form,
      closeRejectCertificateModal,
      refreshCurrentPage
    ]
  );

  useEffect(() => {
    if (!isLoading && certificationApprovals?.data) {
      const tempApproves = INITIAL_APPROVES;

      for (const approval of certificationApprovals.data) {
        if (['secretariat', 'csc'].includes(approval.kind)) {
          const status =
            approval.approved === null
              ? 'pending'
              : approval.approved
              ? 'approved'
              : 'rejected';

          tempApproves[approval.kind as 'secretariat' | 'csc'] = {
            status: status,
            date: approval.updated_at,
            user: approval.user
          };
        }
      }

      setApproves(tempApproves);
    }
  }, [certificationApprovals, isLoading]);

  return (
    <>
      <Drawer
        closable={false}
        closeIcon={false}
        open={approvesDrawer}
        onClose={() => closeApprovesDrawer()}
        width={approvesHistory ? 800 : 500}
        title={
          <Flex vertical>
            <Typography.Title level={4} className="!mb-0">
              Aprovações
            </Typography.Title>
            <Typography.Text type="secondary" className="font-normal">
              Aprovações necessárias para a emissão do certificado
            </Typography.Text>
          </Flex>
        }
        extra={
          <Button
            color="default"
            icon={<Icons.CloseOutlined />}
            onClick={() => closeApprovesDrawer()}
          ></Button>
        }
        footer={
          <Button
            color="default"
            icon={<Icons.HistoryOutlined />}
            className="ml-auto !flex"
            onClick={() => openApprovesHistoryDrawer()}
          >
            Histórico de aprovações
          </Button>
        }
      >
        <Flex vertical gap={16}>
          <ApprovesCard
            title="Secretaria"
            status={approves.secretariat.status}
            date={approves.secretariat.date}
            user={approves.secretariat.user}
            onApprove={() => handleApprove('secretariat')}
            onReject={() => {
              setType('secretariat');
              openRejectCertificateModal();
            }}
          />

          <ApprovesCard
            title="Reguladora"
            status={approves.csc.status}
            date={approves.csc.date}
            user={approves.csc.user}
            onApprove={() => handleApprove('csc')}
            onReject={() => {
              setType('csc');
              openRejectCertificateModal();
            }}
          />
        </Flex>

        <DrawerApprovesHistory />
      </Drawer>

      <CustomModal
        title="Confirmar rejeição"
        open={modals.rejectCertificate}
        icon={<Icons.ExclamationCircleOutlined />}
        onCancel={closeRejectCertificateModal}
        width={700}
        okText="Rejeitar certificado"
        onOk={() => form.submit()}
      >
        <Typography.Paragraph>
          Ao rejeitar este certificado, você precisará informar o motivo para
          que o caso possa ser revisado e corrigido. Esta ação também será
          registrada no histórico de aprovações.
        </Typography.Paragraph>

        <Form form={form} layout="vertical" onFinish={() => handleReject(type)}>
          <Form.Item
            name="observation"
            label="Informe o motivo da rejeição (obrigatório)"
            rules={[{ required: true, message: 'Campo obrigatório' }]}
          >
            <Input.TextArea
              rows={4}
              placeholder="Ex: O arquivo de CPF enviado pelo aluno estava incorreto"
            />
          </Form.Item>
        </Form>
      </CustomModal>
    </>
  );
};

type ApprovesCardProps = {
  title: string;
  status: string;
  date: string;
  user: {
    id: string;
    login: string;
    name: string;
  } | null;
  onApprove: () => void;
  onReject: () => void;
};

const ApprovesCard = ({
  title,
  status,
  date,
  user,
  onApprove,
  onReject
}: ApprovesCardProps) => {
  const isApproved = status === 'approved';
  const isRejected = status === 'rejected';
  const [approveConfirmOpen, setApproveConfirmOpen] = useState(false);
  return (
    <Card
      className={clsx(
        'relative overflow-hidden',
        isApproved && 'bg-green-50 border-green-200',
        isRejected && 'bg-red-50 border-red-200',
        !isApproved && !isRejected && 'bg-neutral-100 border-neutral-200'
      )}
    >
      <Flex vertical gap={16}>
        <div>
          <Typography.Title level={5}>{title}</Typography.Title>
          <Typography.Text>
            {status === 'approved'
              ? `Aprovado em ${formatDate(date, 'DD/MM/YYYY')} por ${
                  user?.name
                }`
              : status === 'rejected'
              ? `Rejeitado em ${formatDate(date, 'DD/MM/YYYY')} por ${
                  user?.name
                }`
              : 'Aguardando aprovação'}
          </Typography.Text>
        </div>
        <Space>
          <Button
            icon={<Icons.CheckCircleOutlined />}
            variant={isApproved ? 'solid' : 'outlined'}
            color={isApproved ? 'green' : 'default'}
            onClick={isApproved ? undefined : () => setApproveConfirmOpen(true)}
            aria-disabled={isApproved}
            tabIndex={isApproved ? -1 : 0}
          >
            {isApproved ? 'Aprovado' : 'Aprovar'}
          </Button>
          <Button
            icon={<Icons.CloseCircleOutlined />}
            variant={isRejected ? 'solid' : 'outlined'}
            color={isRejected ? 'danger' : 'default'}
            onClick={isRejected ? undefined : onReject}
            aria-disabled={isRejected}
            tabIndex={isRejected ? -1 : 0}
          >
            {isRejected ? 'Rejeitado' : 'Rejeitar'}
          </Button>
        </Space>
      </Flex>

      {status === 'approved' && (
        <Icons.CheckCircleOutlined
          className="absolute -bottom-3 -right-5"
          style={{ fontSize: 94, color: 'green', opacity: 0.2 }}
        />
      )}

      {status === 'rejected' && (
        <Icons.CloseCircleOutlined
          className="absolute -bottom-3 -right-5"
          style={{ fontSize: 94, color: 'red', opacity: 0.2 }}
        />
      )}
      <CustomModal
        title="Confirmar aprovação"
        icon={<Icons.ExclamationCircleOutlined />}
        open={approveConfirmOpen}
        onCancel={() => setApproveConfirmOpen(false)}
        okText="Aprovar certificado"
        onOk={() => {
          onApprove();
          setApproveConfirmOpen(false);
        }}
      >
        <Typography.Paragraph>
          Deseja realmente aprovar este certificado? Esta ação será registrada
          no histórico de aprovações.
        </Typography.Paragraph>
      </CustomModal>
    </Card>
  );
};
